import React, { useRef, useState } from 'react';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Upload, Camera, FileImage, ImagePlus, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

import { getAuthToken } from '../../services/auth';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const GENDER_OPTIONS = [
  { value: 'male', label: 'Male' },
  { value: 'female', label: 'Female' },
  { value: 'other', label: 'Other' },
];

export function StaffFormModal({ open, onOpenChange, staffForm, setStaffForm, onReload }) {
  const [idDocFile, setIdDocFile] = useState(null);
  const [photoFile, setPhotoFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const idDocRef = useRef(null);
  const photoRef = useRef(null);

  const getToken = () => getAuthToken();

  const resetForm = () => {
    setIdDocFile(null);
    setPhotoFile(null);
  };

  const handleCreateWithUploads = async () => {
    if (!staffForm.name || !staffForm.id_number || !staffForm.assigned_villas) {
      toast.error('Please fill all required fields (Name, ID Number, Assigned Villas)');
      return;
    }

    setSaving(true);
    try {
      const villas = staffForm.assigned_villas.split(',').map(v => v.trim()).filter(v => v);
      if (villas.length === 0) { toast.error('Please enter at least one villa number'); setSaving(false); return; }

      const body = {
        name: staffForm.name,
        id_number: staffForm.id_number,
        id_type: staffForm.id_type || 'Aadhaar',
        assigned_villas: villas,
        job_description: staffForm.job_description || 'General',
        phone: staffForm.phone || '',
        emergency_contact: staffForm.emergency_contact || '',
        employee_id: staffForm.employee_id || null,
        gender: staffForm.gender || null,
        staff_type: staffForm.staff_type || null,
      };

      const res = await fetch(`${API_URL}/staff`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${getToken()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });

      if (!res.ok) {
        const err = await res.json();
        toast.error(err.detail || 'Failed to register staff');
        setSaving(false);
        return;
      }
      const staffData = await res.json();

      if (idDocFile) {
        const fd = new FormData();
        fd.append('file', idDocFile);
        const idRes = await fetch(`${API_URL}/staff/${staffData.id}/upload-id`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${getToken()}` },
          body: fd
        });
        if (!idRes.ok) toast.error('Staff created but ID document upload failed.');
      }

      if (photoFile) {
        const fd = new FormData();
        fd.append('file', photoFile);
        const photoRes = await fetch(`${API_URL}/staff/${staffData.id}/upload-photo`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${getToken()}` },
          body: fd
        });
        if (!photoRes.ok) toast.error('Staff created but photo upload failed.');
      }

      toast.success('Staff registered successfully!');
      onOpenChange(false);
      setStaffForm({ name: '', id_number: '', id_type: 'Aadhaar', assigned_villas: '', job_description: '', phone: '', emergency_contact: '', employee_id: '', gender: '', staff_type: '' });
      resetForm();
      if (onReload) onReload();
    } catch (err) {
      console.error('Create staff error:', err);
      toast.error('Failed to register staff');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => { onOpenChange(isOpen); if (!isOpen) resetForm(); }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Register Staff/Maid</DialogTitle>
          <DialogDescription>Add a new staff member to the community. Upload documents to complete the registration.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="staff-name">Name *</Label>
              <Input id="staff-name" data-testid="staff-name-input" value={staffForm.name} onChange={(e) => setStaffForm({...staffForm, name: e.target.value})} placeholder="Full name" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="staff-employee-id">Employee ID / Management Issued ID</Label>
              <Input id="staff-employee-id" data-testid="staff-employee-id-input" value={staffForm.employee_id || ''} onChange={(e) => setStaffForm({...staffForm, employee_id: e.target.value})} placeholder="e.g., EMP-001" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="staff-id-number">ID Number *</Label>
              <Input id="staff-id-number" data-testid="staff-id-number-input" value={staffForm.id_number} onChange={(e) => setStaffForm({...staffForm, id_number: e.target.value})} placeholder="Aadhaar/ID number" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="staff-id-type">ID Type</Label>
              <select
                data-testid="staff-id-type-select"
                value={staffForm.id_type}
                onChange={(e) => setStaffForm({...staffForm, id_type: e.target.value})}
                className="flex h-9 w-full items-center rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-ring"
              >
                <option value="Aadhaar">Aadhaar</option>
                <option value="Voter ID">Voter ID</option>
                <option value="Driving License">Driving License</option>
                <option value="PAN Card">PAN Card</option>
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="staff-phone">Phone</Label>
              <Input id="staff-phone" data-testid="staff-phone-input" value={staffForm.phone} onChange={(e) => setStaffForm({...staffForm, phone: e.target.value})} placeholder="+91-XXXXXXXXXX" />
            </div>
            <div className="space-y-2">
              <Label>Gender</Label>
              <div className="flex gap-3 pt-1">
                {GENDER_OPTIONS.map((opt) => (
                  <label key={opt.value} className="flex items-center gap-1.5 cursor-pointer">
                    <input
                      type="radio"
                      name="staff-gender"
                      data-testid={`staff-gender-${opt.value}`}
                      value={opt.value}
                      checked={staffForm.gender === opt.value}
                      onChange={(e) => setStaffForm({...staffForm, gender: e.target.value})}
                      className="w-4 h-4 text-gk-teal focus:ring-gk-teal border-slate-300"
                    />
                    <span className="text-sm">{opt.label}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="staff-villas">Assigned Villas * (comma-separated)</Label>
            <Input id="staff-villas" data-testid="staff-villas-input" value={staffForm.assigned_villas} onChange={(e) => setStaffForm({...staffForm, assigned_villas: e.target.value})} placeholder="e.g., A-101, A-102, B-205" />
            <p className="text-xs text-slate-500">Enter villa numbers separated by commas</p>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="staff-job">Job Description</Label>
              <Input id="staff-job" data-testid="staff-job-input" value={staffForm.job_description} onChange={(e) => setStaffForm({...staffForm, job_description: e.target.value})} placeholder="e.g., Housekeeping, Cook" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="staff-emergency">Emergency Contact</Label>
              <Input id="staff-emergency" data-testid="staff-emergency-input" value={staffForm.emergency_contact} onChange={(e) => setStaffForm({...staffForm, emergency_contact: e.target.value})} placeholder="+91-XXXXXXXXXX" />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="staff-type">Staff Type *</Label>
            <select
              id="staff-type"
              data-testid="staff-type-select"
              value={staffForm.staff_type || ''}
              onChange={(e) => setStaffForm({...staffForm, staff_type: e.target.value})}
              className="w-full h-10 px-3 text-sm border border-slate-300 rounded-md bg-white"
            >
              <option value="">Select staff type…</option>
              <option value="office_staff">Office Staff (society-employed: gardener, sweeper, manager etc.)</option>
              <option value="resident_staff">Resident's Staff (resident-employed: maid, cook, driver etc.)</option>
              <option value="both">Both (serves community office AND residents)</option>
            </select>
          </div>

          {/* ID Document Upload */}
          <div className="space-y-1.5">
            <Label>ID Document Upload</Label>
            <input ref={idDocRef} type="file" accept="image/*,application/pdf" onChange={(e) => setIdDocFile(e.target.files?.[0] || null)} className="hidden" data-testid="staff-id-doc-file-input" />
            <div
              data-testid="staff-id-doc-upload-area"
              onClick={() => idDocRef.current?.click()}
              className={`border-2 border-dashed rounded-lg p-3 text-center cursor-pointer transition-colors ${idDocFile ? 'border-emerald-400 bg-emerald-50' : 'border-slate-300 hover:border-gk-teal hover:bg-slate-50'}`}
            >
              {idDocFile ? (
                <div className="flex items-center justify-center gap-2">
                  <FileImage className="w-4 h-4 text-emerald-600" />
                  <span className="text-sm text-emerald-700 font-medium truncate max-w-[250px]">{idDocFile.name}</span>
                  <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                </div>
              ) : (
                <div className="flex flex-col items-center gap-1">
                  <Upload className="w-5 h-5 text-slate-400" />
                  <span className="text-xs text-slate-500">Click to upload ID document (Image/PDF)</span>
                </div>
              )}
            </div>
          </div>

          {/* Staff Photo Upload */}
          <div className="space-y-1.5">
            <Label>Staff Photo</Label>
            <input ref={photoRef} type="file" accept="image/*" capture="user" onChange={(e) => setPhotoFile(e.target.files?.[0] || null)} className="hidden" data-testid="staff-photo-file-input" />
            <div
              data-testid="staff-photo-upload-area"
              onClick={() => photoRef.current?.click()}
              className={`border-2 border-dashed rounded-lg p-3 text-center cursor-pointer transition-colors ${photoFile ? 'border-emerald-400 bg-emerald-50' : 'border-slate-300 hover:border-gk-teal hover:bg-slate-50'}`}
            >
              {photoFile ? (
                <div className="flex items-center justify-center gap-2">
                  <ImagePlus className="w-4 h-4 text-emerald-600" />
                  <span className="text-sm text-emerald-700 font-medium truncate max-w-[250px]">{photoFile.name}</span>
                  <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                </div>
              ) : (
                <div className="flex flex-col items-center gap-1">
                  <Camera className="w-5 h-5 text-slate-400" />
                  <span className="text-xs text-slate-500">Click to upload or take a photo</span>
                </div>
              )}
            </div>
          </div>

          <div className="flex gap-2">
            <Button
              data-testid="submit-staff-button"
              onClick={handleCreateWithUploads}
              className="flex-1 bg-gk-teal hover:bg-gk-teal-hover"
              disabled={saving}
            >
              {saving ? 'Registering...' : 'Register Staff'}
            </Button>
            <Button onClick={() => { onOpenChange(false); resetForm(); }} variant="outline">
              Cancel
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
